/**
 * Print configuration dialog + `usePrint()` hook.
 *
 * Caller renders `{dialog}` once in their tree and calls `openPrintDialog`
 * with a `PrintRequest`. The dialog walks the user through paper,
 * margins, scale, pages and color, then either:
 *   - composes a HTML iframe (image / html jobs), or
 *   - builds a new PDF via pdf-lib that embeds the source pages with the
 *     chosen layout (PDF jobs — keeps text vector-sharp)
 *
 * and triggers the system print dialog via `runPrint`.
 */

import { FileText, Image as ImageIcon, Loader2, Printer, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { fetchPdfBytes, recomposePdf } from "./pdf-recomposer.js";
import { buildPrintHtml, runPrint } from "./print-service.js";
import {
  DEFAULT_SETTINGS,
  type MarginPreset,
  type PaperSize,
  type PrintRequest,
  type PrintSettings,
  parsePageRange,
} from "./print-types.js";

/* ─────────────────────── hook ─────────────────────── */

export function usePrint() {
  const [request, setRequest] = useState<PrintRequest | null>(null);
  const resolveRef = useRef<(() => void) | null>(null);

  const openPrintDialog = useCallback((req: PrintRequest) => {
    return new Promise<void>((resolve) => {
      resolveRef.current = resolve;
      setRequest(req);
    });
  }, []);

  const close = useCallback(() => {
    setRequest(null);
    resolveRef.current?.();
    resolveRef.current = null;
  }, []);

  const dialog = request ? (
    <PrintDialog key={request.title} request={request} onClose={close} />
  ) : null;

  return { openPrintDialog, dialog };
}

/* ─────────────────────── dialog ─────────────────────── */

interface PrintDialogProps {
  request: PrintRequest;
  onClose: () => void;
}

function PrintDialog({ request, onClose }: PrintDialogProps) {
  // PDFs already ship with their own margins baked into each page;
  // adding ours on top causes visible double-padding. Default to "none"
  // for PDFs and let the user bump it up if they really want extra.
  const kindDefaults: Partial<PrintSettings> = request.job.kind === "pdf" ? { margin: "none" } : {};
  const initial: PrintSettings = {
    ...DEFAULT_SETTINGS,
    ...kindDefaults,
    ...request.defaults,
  };
  const [settings, setSettings] = useState<PrintSettings>(initial);
  const [stage, setStage] = useState<"config" | "preparing" | "error">("config");
  const [error, setError] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  const job = request.job;
  const isPdf = job.kind === "pdf";
  const totalPages = isPdf ? job.totalPages : 1;

  /** Compute the final 1-based page list for the current settings. */
  function resolvePages(): number[] {
    if (!isPdf) return [1];
    if (settings.pageMode === "all") {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (settings.pageMode === "current") {
      return [job.currentPage];
    }
    return parsePageRange(settings.pageRangeText, totalPages);
  }

  function update<K extends keyof PrintSettings>(key: K, value: PrintSettings[K]) {
    setSettings((s) => ({ ...s, [key]: value }));
  }

  async function startPrint() {
    setError(null);
    try {
      if (isPdf) {
        const pages = resolvePages();
        if (pages.length === 0) {
          setError("Bitte gültigen Seitenbereich angeben.");
          return;
        }
        setStage("preparing");
        const bytes = await fetchPdfBytes(job.src);
        const out = await recomposePdf({
          srcBytes: bytes,
          pages,
          paper: settings.paper,
          orientation: settings.orientation,
          margin: settings.margin,
          scalePct: settings.scalePct,
        });
        // pdf-lib returns a fresh Uint8Array — wrap as Blob and feed to
        // the iframe via blob URL so the browser's native PDF print
        // pipeline takes over (keeps vector quality + selectable text).
        const blob = new Blob([out.buffer as ArrayBuffer], {
          type: "application/pdf",
        });
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        await runPrint({ kind: "pdf-blob", blobUrl });
        // Revoke shortly after — long enough for the iframe to have
        // started loading the PDF in the system print dialog.
        window.setTimeout(() => {
          if (blobUrlRef.current === blobUrl) {
            URL.revokeObjectURL(blobUrl);
            blobUrlRef.current = null;
          }
        }, 60_000);
      } else {
        const html = buildPrintHtml({ title: request.title, job, settings });
        await runPrint({ kind: "html", html });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStage("error");
    }
  }

  /* ─────────────────────── render ─────────────────────── */

  return (
    <div
      className="fixed inset-0 z-300 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      onClick={stage === "config" ? onClose : undefined}
      role="presentation"
    >
      <div
        className="glass-floating w-full max-w-xl rounded-3xl border border-white/10 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="print-dialog-title"
      >
        {/* Header */}
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-pitch-lime/15 text-pitch-lime">
              <Printer className="size-5" />
            </div>
            <div className="min-w-0">
              <h2 id="print-dialog-title" className="text-body-md font-semibold leading-tight">
                Drucken
              </h2>
              <p
                className="mt-0.5 truncate text-[12px]"
                style={{ color: "var(--color-on-surface-variant)" }}
                title={request.title}
              >
                {isPdf ? (
                  <FileText className="mr-1 inline size-3" />
                ) : (
                  <ImageIcon className="mr-1 inline size-3" />
                )}
                {request.title}
              </p>
            </div>
          </div>
          {stage === "config" && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className="rounded-lg p-1.5 text-white/70 hover:bg-white/10"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        {/* Body */}
        {stage === "config" && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Papierformat">
                <Select
                  value={settings.paper}
                  onChange={(v) => update("paper", v as PaperSize)}
                  options={[
                    { value: "A4", label: "A4" },
                    { value: "A3", label: "A3" },
                    { value: "A5", label: "A5" },
                    { value: "Letter", label: "US Letter" },
                    { value: "Legal", label: "US Legal" },
                  ]}
                />
              </Field>
              <Field label="Ausrichtung">
                <Segmented
                  value={settings.orientation}
                  onChange={(v) => update("orientation", v as "portrait" | "landscape")}
                  options={[
                    { value: "portrait", label: "Hoch" },
                    { value: "landscape", label: "Quer" },
                  ]}
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Ränder">
                <Select
                  value={settings.margin}
                  onChange={(v) => update("margin", v as MarginPreset)}
                  options={[
                    { value: "none", label: "Keine" },
                    { value: "narrow", label: "Schmal (8 mm)" },
                    { value: "normal", label: "Normal (15 mm)" },
                    { value: "wide", label: "Breit (25 mm)" },
                  ]}
                />
              </Field>
              <Field label={`Skalierung (${settings.scalePct} %)`}>
                <input
                  type="range"
                  min={25}
                  max={200}
                  step={5}
                  value={settings.scalePct}
                  onChange={(e) => update("scalePct", Number.parseInt(e.target.value, 10))}
                  className="w-full accent-pitch-lime"
                />
              </Field>
            </div>

            {/* PDF-only: page range */}
            {isPdf && (
              <Field label="Seiten">
                <div className="space-y-2">
                  <Segmented
                    value={settings.pageMode}
                    onChange={(v) => update("pageMode", v as "all" | "current" | "range")}
                    options={[
                      { value: "all", label: `Alle (${totalPages})` },
                      {
                        value: "current",
                        label: `Aktuelle (${job.currentPage})`,
                      },
                      { value: "range", label: "Bereich" },
                    ]}
                  />
                  {settings.pageMode === "range" && (
                    <input
                      type="text"
                      value={settings.pageRangeText}
                      placeholder="z.B. 1-3, 5, 7-9"
                      onChange={(e) => update("pageRangeText", e.target.value)}
                      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[13px] outline-none focus:border-pitch-lime/60"
                    />
                  )}
                </div>
              </Field>
            )}

            {/* Color toggle — image-only. For PDF use the OS-level B&W
                option in the system print dialog (pdf-lib has no
                grayscale filter for embedded vector content). */}
            {!isPdf && (
              <Field label="Farbe">
                <Segmented
                  value={settings.grayscale ? "gray" : "color"}
                  onChange={(v) => update("grayscale", v === "gray")}
                  options={[
                    { value: "color", label: "Farbe" },
                    { value: "gray", label: "Graustufen" },
                  ]}
                />
              </Field>
            )}

            {isPdf && (
              <p
                className="text-[11px] leading-snug"
                style={{ color: "var(--color-on-surface-variant)" }}
              >
                Hinweis: Graustufen-Druck und Anzahl der Kopien werden im anschließenden
                System-Druckdialog gewählt.
              </p>
            )}

            {error && (
              <p className="text-[12px]" style={{ color: "#ff8a8a" }}>
                {error}
              </p>
            )}

            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 items-center justify-center rounded-full bg-white/6 px-4 text-body-sm hover:bg-white/10"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={startPrint}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] shadow-glow hover:brightness-110"
              >
                <Printer className="size-4" /> Drucken
              </button>
            </div>
          </div>
        )}

        {stage === "preparing" && (
          <div className="py-8">
            <div className="flex flex-col items-center gap-3 text-center">
              <Loader2 className="size-8 animate-spin text-pitch-lime" />
              <p className="text-body-sm font-semibold">Druck wird vorbereitet …</p>
              <p className="text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
                Dokument wird mit deinen Einstellungen aufbereitet.
              </p>
            </div>
          </div>
        )}

        {stage === "error" && (
          <div className="py-4">
            <p className="text-body-sm" style={{ color: "#ff8a8a" }}>
              {error || "Druck fehlgeschlagen."}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 items-center justify-center rounded-full bg-white/6 px-4 text-body-sm hover:bg-white/10"
              >
                Schließen
              </button>
              <button
                type="button"
                onClick={() => {
                  setStage("config");
                  setError(null);
                }}
                className="inline-flex h-10 items-center justify-center rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] hover:brightness-110"
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────── tiny field primitives ─────────────────────── */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span
        className="mb-1 block text-[11px] font-semibold uppercase tracking-wide"
        style={{ color: "var(--color-on-surface-variant)" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[13px] outline-none focus:border-pitch-lime/60"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-[#1d2420]">
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Segmented({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div
      className="inline-flex w-full overflow-hidden rounded-lg border border-white/10"
      style={{ background: "rgba(255,255,255,0.03)" }}
      role="tablist"
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className="flex-1 px-3 py-1.5 text-[12px] font-semibold transition"
            style={{
              background: active ? "rgba(178,255,89,0.16)" : "transparent",
              color: active ? "var(--color-pitch-lime)" : "rgba(255,255,255,0.75)",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
